/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import AdmissionEnquiryModal from "./AdmissionEnquiryModal";

const countries = {
  Russia: "russia",
  Georgia: "georgia",
  Uzbekistan: "uzbekistan",
  Malaysia: "malaysia",
  Nepal: "nepal",
};

const countryTagMap = Object.fromEntries(
  Object.entries(countries).map(([country, tag]) => [tag, country])
);

const GHOST_API_URL =
  process.env.NEXT_PUBLIC_GHOST_API_URL ;
const GHOST_API_KEY =
  process.env.NEXT_PUBLIC_GHOST_API_KEY;

const ACTIVE_COUNTRY_STORAGE_KEY = "mbbs-exclusive-partners-active-country";

const countryFlags = {
  Russia: "/assets/img/ug_abroad/Russia.jpg",
  Georgia: "/assets/img/ug_abroad/Georgia.jpg",
  Uzbekistan: "/assets/img/ug_abroad/uzbekistan.png",
  Malaysia: "/assets/img/ug_abroad/malaysia.png",
  Nepal: "/assets/img/ug_abroad/Nepal-Flag.jpg",
};

const countryCodes = {
  australia: "AU",
  bangladesh: "BD",
  belarus: "BY",
  canada: "CA",
  china: "CN",
  georgia: "GE",
  germany: "DE",
  guyana: "GY",
  hungary: "HU",
  ireland: "IE",
  japan: "JP",
  kazakhstan: "KZ",
  kyrgyzstan: "KG",
  luxembourg: "LU",
  malaysia: "MY",
  nepal: "NP",
  netherlands: "NL",
  "new-zealand": "NZ",
  philippines: "PH",
  romania: "RO",
  russia: "RU",
  uae: "AE",
  "united-arab-emirates": "AE",
  "united-kingdom": "GB",
  "united-states": "US",
  uzbekistan: "UZ",
  vietnam: "VN",
};

const parseCollegeDetails = (excerpt) => {
  if (!excerpt || typeof excerpt !== "string") return {};

  const trimmedExcerpt = excerpt.trim();
  if (!trimmedExcerpt.startsWith("{") && !trimmedExcerpt.startsWith("[")) {
    return {};
  }

  try {
    return JSON.parse(trimmedExcerpt);
  } catch {
    return {};
  }
};

const normalizeValue = (value) => (value || "").toString().trim().toLowerCase();

const formatCountryName = (value) =>
  (value || "")
    .toString()
    .trim()
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const normalizeTag = (value) =>
  normalizeValue(value)
    .replace(/\s+/g, "-")
    .replace(/&/g, "and");

let isoCountryCodeMap;

const getIsoCountryCodeMap = () => {
  if (isoCountryCodeMap) return isoCountryCodeMap;

  isoCountryCodeMap = { ...countryCodes };

  if (typeof Intl?.DisplayNames !== "function") return isoCountryCodeMap;

  const displayNames = new Intl.DisplayNames(["en"], { type: "region" });

  for (let first = 65; first <= 90; first += 1) {
    for (let second = 65; second <= 90; second += 1) {
      const code = String.fromCharCode(first, second);
      const countryName = displayNames.of(code);

      if (countryName && countryName !== code) {
        isoCountryCodeMap[normalizeTag(countryName)] = code;
      }
    }
  }

  return isoCountryCodeMap;
};

const resolveCountryCode = (country) =>
  getIsoCountryCodeMap()[normalizeTag(country).replace(/^mbbs-in-/, "")];

const getCountryFlag = (country) => {
  const code = resolveCountryCode(country);
  if (!code) return "🏳️";

  return code
    .split("")
    .map((letter) => String.fromCodePoint(127397 + letter.charCodeAt(0)))
    .join("");
};

const NON_COUNTRY_TAGS = new Set([
  "college",
  "colleges",
  "mbbs",
  "mbbs-abroad",
  "study-abroad",
  "featured",
  "university",
  "universities",
]);

const getGhostTagSlugs = (post) => {
  if (!Array.isArray(post?.tags)) return [];

  return post.tags
    .map((tag) => tag.slug || tag.name)
    .filter(Boolean)
    .map(normalizeTag);
};

const getCollegeCountry = (details, post) => {
  const countryFromDetails =
    details?.country ||
    details?.countryName ||
    details?.countryOrRegion ||
    details?.region;

  const matchingCountry = Object.keys(countries).find(
    (country) => normalizeValue(country) === normalizeValue(countryFromDetails)
  );

  if (matchingCountry) return matchingCountry;
  if (countryFromDetails && resolveCountryCode(countryFromDetails)) {
    return formatCountryName(countryFromDetails);
  }

  const countryTag = getGhostTagSlugs(post).find(
    (tagSlug) => countryTagMap[tagSlug]
  );

  if (countryTag) return countryTagMap[countryTag];

  // New countries do not need to be added to the hard-coded map. For a
  // college post, use its country tag (for example "kyrgyzstan" or
  // "mbbs-in-kyrgyzstan") as the country name.
  const hasCollegeDetails = [
    "city",
    "location",
    "fees",
    "tuition",
    "establishedYear",
    "established_year",
    "established",
    "rank",
    "worldRank",
  ].some((field) => details?.[field]);

  if (!hasCollegeDetails) return "";

  const dynamicCountryTag = getGhostTagSlugs(post).find(
    (tagSlug) =>
      tagSlug &&
      !tagSlug.startsWith("#") &&
      !NON_COUNTRY_TAGS.has(tagSlug) &&
      resolveCountryCode(tagSlug)
  );

  return dynamicCountryTag
    ? formatCountryName(dynamicCountryTag.replace(/^mbbs-in-/, ""))
    : "";
};

const mapGhostCollege = (post) => {
  const details = parseCollegeDetails(post.custom_excerpt);

  return {
    id: post.id,
    name: post.title,
    city: details.city || details.location || "",
    tuition: details.fees || details.tuition || "Contact us",
    est:
      details.establishedYear ||
      details.established_year ||
      details.established ||
      "",
    rank: details.rank || details.worldRank || "",
    img:
      post.feature_image?.replace(/ghost\.neetsupport\.com/g, "neetsupport.com") ||
      "/assets/img/ug_abroad/siberian.webp",
    link: `/college/${post.slug}`,
  };
};

function UniCard({ uni, country, onApply }) {
  return (
    <div className="uni-card">
      <div className="card-link">
        <div className="card-img-wrap">
          <img src={uni.img} alt={uni.name} className="card-img" />
        </div>

        <div className="card-body">
          <h6 className="card-title">{uni.name}</h6>
          <p className="card-course">Course: MBBS</p>

          <div className="card-meta">
            <div className="meta-item">
              <span aria-hidden="true">📍</span>
              <span>{[uni.city, country].filter(Boolean).join(", ")}</span>
            </div>
            <div className="meta-item">
              <span aria-hidden="true">💰</span>
              <span>Fees: {uni.tuition}</span>
            </div>
            {uni.est && (
              <div className="meta-item">
                <span aria-hidden="true">🏛️</span>
                <span>Est. {uni.est}</span>
              </div>
            )}
            {uni.rank && (
              <div className="meta-item">
                <span aria-hidden="true">🌍</span>
                <span>Rank: {uni.rank}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card-actions">
        <a className="btn-outline" href={uni.link}>
          View Details
        </a>
        <button className="btn-dark" onClick={() => onApply && onApply(uni)}>Apply Now</button>
      </div>
    </div>
  );
}

function Slider({ country, universities, onApply }) {

  const [index, setIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);

  const repeatedUniversities = [...universities, ...universities];
  const maxIndex = universities.length || 1;

  useEffect(() => setIndex(0), [country]);

  // Responsive visible count
  useEffect(() => {
    function update() {
      const w = window.innerWidth;
      if (w >= 1200) setVisibleCount(3);
      else if (w >= 768) setVisibleCount(2);
      else setVisibleCount(1);
    }
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Auto slide
  useEffect(() => {
    if (!universities.length) return undefined;
    const interval = setInterval(() => setIndex((p) => p + 1), 3000);
    return () => clearInterval(interval);
  }, [universities.length]);

  // Infinite reset
  useEffect(() => {
    if (index >= maxIndex) {
      const t = setTimeout(() => setIndex(0), 400);
      return () => clearTimeout(t);
    }
  }, [index, maxIndex]);

  const prev = () => setIndex((p) => (p <= 0 ? maxIndex - 1 : p - 1));
  const next = () => setIndex((p) => p + 1);

  return (
    <div className="slider-wrapper">
      <button aria-label="Previous" className="arrow arrow-prev" onClick={prev}>
        ❮
      </button>

      <div className="slider-track-outer">
        <div
          className="slider-track"
          style={{
            transform: `translateX(-${index * (100 / visibleCount)}%)`,
            transition: index >= maxIndex ? "none" : "transform 0.6s ease",
          }}
        >
          {repeatedUniversities.map((uni, i) => (
            <div
              key={`${uni.id}-${i}`}
              className="slide"
              style={{ flex: `0 0 ${100 / visibleCount}%`, minWidth: `${100 / visibleCount}%` }}
            >
              <UniCard uni={uni} country={country} onApply={onApply} />
            </div>
          ))}
        </div>
      </div>

      <button aria-label="Next" className="arrow arrow-next" onClick={next}>
        ❯
      </button>
    </div>
  );
}

export default function MbbsExclusivePartners() {
  const [activeCountry, setActiveCountry] = useState("Russia");
  const [countriesData, setCountriesData] = useState({});
  const [loadingColleges, setLoadingColleges] = useState(true);
  const [collegeError, setCollegeError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: activeCountry });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const storedCountry = sessionStorage.getItem(ACTIVE_COUNTRY_STORAGE_KEY);
    if (storedCountry) setActiveCountry(storedCountry);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    async function loadColleges() {
      const params = new URLSearchParams({
        key: GHOST_API_KEY,
        include: "tags",
        fields: "id,slug,title,feature_image,custom_excerpt",
        limit: "all",
      });

      try {
        const response = await fetch(
          `${GHOST_API_URL.replace(/\/+$/, "")}/ghost/api/content/posts/?${params}`,
          { signal: controller.signal }
        );

        if (!response.ok) throw new Error(`Ghost API returned ${response.status}`);

        const { posts = [] } = await response.json();
        const groupedColleges = {};

        posts.forEach((post) => {
          const details = parseCollegeDetails(post.custom_excerpt);
          const country = getCollegeCountry(details, post);
          if (!country) return;

          if (!groupedColleges[country]) groupedColleges[country] = [];
          groupedColleges[country].push(mapGhostCollege(post));
        });

        setCountriesData(groupedColleges);
        const availableCountries = Object.keys(groupedColleges);
        if (availableCountries.length) {
          setActiveCountry((currentCountry) =>
            groupedColleges[currentCountry] ? currentCountry : availableCountries[0]
          );
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Unable to load colleges from Ghost:", error);
          setCollegeError("Unable to load the latest colleges right now.");
        }
      } finally {
        if (!controller.signal.aborted) setLoadingColleges(false);
      }
    }

    loadColleges();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    setForm((f) => ({ ...f, country: activeCountry }));

    if (typeof window !== "undefined") {
      sessionStorage.setItem(ACTIVE_COUNTRY_STORAGE_KEY, activeCountry);
    }
  }, [activeCountry]);

  const handleApply = (uni) => {
    setForm({ name: "", email: "", phone: "", country: uni?.name ? `${uni.name}, ${activeCountry}` : activeCountry });
    setModalOpen(true);
  };

  const handleClose = () => setModalOpen(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // simulate submission; replace with real API call when available
      await new Promise((r) => setTimeout(r, 800));
      setMessage("Your enquiry has been received.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mbbs-section">
      <style>{`
        .mbbs-section {
          font-family: 'Segoe UI', sans-serif;
          background: linear-gradient(180deg, #ffffff 0%, #fff6ef 100%);
          padding: 24px 0 40px;
        }

        .container {
          max-width: 1380px;
          margin: 0 auto;
          padding: 0 16px;
        }

        .section-title {
          font-size: 2.2rem;
          font-weight: 800;
          text-align: center;
          margin-bottom: 28px;
        }

        .country-scroll {
          display: flex;
          gap: 10px;
          overflow-x: auto;
          margin-bottom: 20px;
        }

        .country-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 999px;
          border: 1px solid #ffd6be;
          background: #fff;
          cursor: pointer;
          transition: 0.3s;
          white-space: nowrap;
        }

        .country-btn.active {
          background: #ff9245;
          color: #fff;
        }

        .country-btn img {
          width: 22px;
          height: 22px;
          border-radius: 50%;
        }

        .country-flag-emoji {
          width: 22px;
          font-size: 20px;
          line-height: 1;
          text-align: center;
        }

        .slider-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .slider-track-outer {
          overflow: hidden;
          flex: 1;
        }

        .slider-track {
          display: flex;
          align-items: stretch;
          gap: 12px;
        }

        .slide {
          box-sizing: border-box;
          display: flex;
          flex-shrink: 0;
          padding: 2px 6px 4px;
        }

        .uni-card {
          display: flex;
          flex-direction: column;
          width: 100%;
          height: 100%;
          background: #fff;
          padding: 14px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.09);
          transition: box-shadow 0.3s ease, transform 0.3s ease;
        }

        .uni-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 22px rgba(15, 23, 42, 0.13);
        }

        .card-link {
          display: flex;
          flex: 1;
          flex-direction: column;
        }

        .card-img-wrap {
          position: relative;
          flex-shrink: 0;
          width: 100%;
          aspect-ratio: 16 / 9;
          overflow: hidden;
          border-radius: 8px;
          background: #f3f4f6;
        }

        .card-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }

        .card-body {
          display: flex;
          flex: 1;
          flex-direction: column;
          padding: 14px 0 0;
        }

        .card-title {
          font-size: 1rem;
          font-weight: 700;
          line-height: 1.4;
          margin: 0 0 4px;
          display: -webkit-box;
          overflow: hidden;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }

        .card-course {
          font-size: 0.85rem;
          color: #6b7280;
          margin: 0;
        }

        .card-meta {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          column-gap: 12px;
          row-gap: 10px;
          margin-top: auto;
          padding-top: 14px;
          font-size: 0.85rem;
          line-height: 1.35;
          color: #6b7280;
        }

        .meta-item {
          display: flex;
          align-items: flex-start;
          gap: 6px;
          min-width: 0;
        }

        .meta-item span:last-child {
          overflow-wrap: anywhere;
        }

        .card-actions {
          display: flex;
          flex-shrink: 0;
          gap: 8px;
          align-items: center;
          padding-top: 16px;
        }

        .btn-dark,
        .btn-outline {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 1;
          padding: 8px 10px;
          border-radius: 999px;
          min-height: 38px;
          text-align: center;
          text-decoration: none;
          font-size: 0.85rem;
          font-weight: 600;
          line-height: 1.2;
          transition: background-color 0.3s ease, color 0.3s ease;
        }

        .btn-dark {
          background: #ff9245;
          border: 1px solid #ff9245;
          color: #fff;
          cursor: pointer;
        }

        .btn-outline {
          border: 1px solid #ff9245;
          color: #ff9245;
          background: #fff;
        }

        .btn-dark:hover {
          background: #fff;
          color: #ff9245;
        }

        .btn-outline:hover {
          background: #ff9245;
          color: #fff;
        }

        .arrow {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: none;
          background: #ff9245;
          color: #fff;
          font-size: 22px;
          cursor: pointer;
          z-index: 10;
        }

        .arrow-prev {
          margin-right: 10px;
        }

        .arrow-next {
          margin-left: 10px;
        }

        @media (max-width: 600px) {
          /* Compact country buttons and reduce text on small screens */
          .country-scroll { gap: 8px; margin-bottom: 12px; }
          .country-btn { padding: 10px 25px; gap: 6px; font-size: 0.8rem; }
          .country-btn img { width: 16px; height: 16px; }
          .section-title { font-size: 1.6rem; }
          .card-title { font-size: 0.95rem; }
          .btn-dark, .btn-outline { font-size: 0.8rem; padding: 8px; }
          .slider-track { gap: 0; }
          .slide { padding: 2px 4px 4px; }
          .slide { min-width: 100% !important; }
          .arrow { display: none; }
        }
      `}</style>

      <div className="container">
        <h3 className="section-title">
          Our Exclusive Study <span style={{ color: "#FF9245" }}>MBBS Abroad Partners</span>
        </h3>

        <div className="country-scroll">
          {Object.keys(countriesData)
            .sort((countryA, countryB) =>
              countryA.localeCompare(countryB, "en", { sensitivity: "base" })
            )
            .map((country) => (
            <button
              key={country}
              className={`country-btn ${activeCountry === country ? "active" : ""}`}
              onClick={() => setActiveCountry(country)}
            >
              {countryFlags[country] ? (
                <img src={countryFlags[country]} alt={`${country} flag`} />
              ) : (
                <span
                  className="country-flag-emoji"
                  role="img"
                  aria-label={`${country} flag`}
                >
                  {getCountryFlag(country)}
                </span>
              )}
              {country}
            </button>
            ))}
        </div>

        {loadingColleges ? (
          <p className="college-status">Loading latest colleges...</p>
        ) : collegeError ? (
          <p className="college-status">{collegeError}</p>
        ) : countriesData[activeCountry]?.length ? (
          <Slider
            country={activeCountry}
            universities={countriesData[activeCountry]}
            onApply={handleApply}
          />
        ) : (
          <p className="college-status">
            No colleges are currently available for {activeCountry}.
          </p>
        )}

        <AdmissionEnquiryModal
          open={modalOpen}
          onClose={handleClose}
          form={form}
          setForm={setForm}
          onSubmit={handleSubmit}
          submitting={submitting}
          message={message}
        />
      </div>
    </section>
  );
}
